/**
 * ModelVersionsSection Component
 *
 * Displays AI model version information for audit items with original content timestamp
 * Extracted from AuditItemContent to improve maintainability and follow component strategy patterns.
 */

import { TimestampDisplay } from '@core/components';
import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { Badge, BadgeSize } from '@soteria/design-system-ve';

import * as styles from '../AuditItem.css';

interface ModelVersionsSectionProps {
  item: GraphQLAuditItem;
}

export function ModelVersionsSection({ item }: ModelVersionsSectionProps) {
  const { slmModelVersion, slmPromptVersion, quicktxtModelVersion } = item.contentSnapshot || {};
  const modelVersions = [];

  if (slmModelVersion) {
    modelVersions.push(`SLM ${slmModelVersion}`);
  }

  if (slmPromptVersion && slmPromptVersion.toLowerCase() !== 'unknown') {
    modelVersions.push(`Prompt ${slmPromptVersion}`);
  }

  if (quicktxtModelVersion) {
    modelVersions.push(`QuickTXT ${quicktxtModelVersion}`);
  }

  // Only show model versions if at least one is available
  if (modelVersions.length === 0) {
    return null;
  }

  return (
    <div className={styles.metadataRow}>
      <div className={styles.metadataItem}>
        <span className={styles.label}>Models:</span>
        <div className={styles.labelBadges}>
          {modelVersions.map(version => (
            <Badge key={version} size={BadgeSize.Small} text={version} />
          ))}
        </div>
      </div>
      <TimestampDisplay className={styles.timestamp} timestamp={item.kustoReference.timestamp} />
    </div>
  );
}
